// Play Selections will be logged by Oscar standing next to the play caller:
// 1. Personnel Group yelled from sideline
// 2. Formation (and direction) signaled from sideline; can be repeated to Oscar
// 3. For run plays the code word and any tags are yelled from the sideline
// 4. For pass plays the code word and number indicate a number on the wristband
// - we can use this value to look up the pass play in the playbook

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useSchemes } from "../../../../contexts/SchemesContext";
import { useLogger } from "../../../../contexts/LoggerContext";
import { OffensivePlaySelection } from "../../../../types/LogTypes";

interface Props {
  clearSelections: boolean;
  setClearSelections: (clearSelections: boolean) => void;
}

export const OffensePlaySelection = ({
  clearSelections,
  setClearSelections,
}: Props) => {
  const { setCurrentPlayCall } = useLogger();
  const {
    formations,
    runConcepts,
    offensivePersonnel,
    formationTags,
    offensivePlayTags,
  } = useSchemes();

  // TODO: create a filter to only include possible run concepts based on the formation
  // direction

  const allRunCodeWords: { id: number; code_word: string }[] = [];
  runConcepts.forEach((runConcept) => {
    allRunCodeWords.push({
      id: runConcept.id,
      code_word: runConcept.code_word_left,
    });
    allRunCodeWords.push({
      id: runConcept.id,
      code_word: runConcept.code_word_right,
    });
  });

  const [selectedPersonnel, setSelectedPersonnel] = useState<number | null>(
    null
  );
  const [selectedFormation, setSelectedFormation] = useState<number | null>(
    null
  );
  const [selectedRunConcept, setSelectedRunConcept] = useState<number | null>(
    null
  );
  const [selectedRunCodeWord, setSelectedRunCodeWord] = useState<string | null>(
    null
  );
  const [selectedFormationTags, setSelectedFormationTags] = useState<number[]>(
    []
  );
  const [selectedAlphaBravoCharlie, setSelectedAlphaBravoCharlie] = useState<
    string | null
  >(null);

  const [selectedWristbandNumber, setSelectedWristbandNumber] = useState<
    number | null
  >(null);

  const [selectedPlayTags, setSelectedPlayTags] = useState<number | null>(null);

  const [formationDirection, setFormationDirection] = useState<
    "Left" | "Right"
  >("Right");

  useEffect(() => {
    const playCall: OffensivePlaySelection = {
      personnel_group_id: selectedPersonnel,
      formation_id: selectedFormation,
      run_concept_id: selectedRunConcept,
      direction: formationDirection,
      formation_tags: selectedFormationTags,
      play_tag_id: selectedPlayTags,
      wristband_number: selectedWristbandNumber,
      alpha_bravo_charlie: selectedAlphaBravoCharlie,
      run_code_word: selectedRunCodeWord,
    };
    setCurrentPlayCall(playCall);
  }, [
    selectedPersonnel,
    selectedFormation,
    selectedRunConcept,
    formationDirection,
    selectedFormationTags,
    selectedPlayTags,
    selectedWristbandNumber,
    selectedAlphaBravoCharlie,
    selectedRunCodeWord,
    setCurrentPlayCall,
  ]);

  useEffect(() => {
    if (clearSelections) {
      setSelectedPersonnel(null);
      setSelectedFormation(null);
      setSelectedRunConcept(null);
      setSelectedRunCodeWord(null);
      setSelectedFormationTags([]);
      setSelectedPlayTags(null);
      setSelectedWristbandNumber(null);
      setSelectedAlphaBravoCharlie(null);
      setClearSelections(false);
    }
  }, [clearSelections]);


  const handlePersonnelChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedPersonnel(event.target.value as number);
  };

  const handleFormationChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedFormation(event.target.value as number);
  };

  const handleFormationTagsChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSelectedFormationTags(
      typeof value === "string"
        ? value.split(",").map(Number)
        : (value as number[])
    );
  };

  const handleDirectionChange = (
    event: SelectChangeEvent<"Left" | "Right">
  ) => {
    setFormationDirection(event.target.value as "Left" | "Right");
  };

  const handleRunConceptChange = (event: SelectChangeEvent<number | "">) => {
    const selectedId = event.target.value as number;
    setSelectedRunConcept(selectedId);

    // Find the corresponding code word for the selected run concept
    const selectedRunCodeWordData = allRunCodeWords.find(
      (runCodeWord) => runCodeWord.id === selectedId
    );
    setSelectedRunCodeWord(selectedRunCodeWordData?.code_word || null);
  };

  const handleAlphaBravoCharlieChange = (
    event: SelectChangeEvent<string | "">
  ) => {
    setSelectedAlphaBravoCharlie(event.target.value as string);
  };

  const handlePlayTagsChange = (event: SelectChangeEvent<number>) => {
    setSelectedPlayTags(event.target.value as number);
  };

  return (
    <Paper elevation={3} sx={{ p: 1 }}>
      <Box>
        <Grid container spacing={3}>
          {/* Personnel Group Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel id="personnel-group-label">
                Personnel Group
              </InputLabel>
              <Select
                labelId="personnel-group-label"
                id="personnel-group"
                value={selectedPersonnel || ""}
                label="Personnel Group"
                onChange={handlePersonnelChange}
              >
                {offensivePersonnel.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Formation Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel id="formation-label">Formation</InputLabel>
              <Select
                labelId="formation-label"
                id="formation"
                value={selectedFormation || ""}
                label="Formation"
                onChange={handleFormationChange}
              >
                {formations.map((formation) => (
                  <MenuItem key={formation.id} value={formation.id}>
                    {formation.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Formation Direction */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel id="formation-direction-label">
                Formation Direction
              </InputLabel>
              <Select
                labelId="formation-direction-label"
                id="formation-direction"
                value={formationDirection}
                label="Formation Direction"
                onChange={handleDirectionChange}
              >
                <MenuItem value="Left">Left</MenuItem>
                <MenuItem value="Right">Right</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Formation Tags */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel id="formation-tags-label">Formation Tags</InputLabel>
              <Select
                labelId="formation-tags-label"
                id="formation-tags"
                value={selectedFormationTags}
                label="Formation Tags"
                onChange={handleFormationTagsChange}
                multiple
              >
                {formationTags.map((formation) => (
                  <MenuItem key={formation.id} value={formation.id}>
                    {formation.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Run Concept Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel id="run-concept-label">Run Concept</InputLabel>
              <Select
                labelId="run-concept-label"
                id="run-concept"
                value={selectedRunConcept || ""}
                label="Run Concept"
                onChange={handleRunConceptChange}
              >
                {allRunCodeWords.map((runCodeWord) => (
                  <MenuItem key={runCodeWord.id} value={runCodeWord.id}>
                    {runCodeWord.code_word}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/*  (Wristband) Alpha Bravo Charlie Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel id="alpha-bravo-charlie-label">
                Pass Concept
              </InputLabel>
              <Select
                labelId="alpha-bravo-charlie-label"
                id="alpha-bravo-charlie"
                value={selectedAlphaBravoCharlie || ""}
                label="Alpha Bravo Charlie"
                onChange={handleAlphaBravoCharlieChange}
              >
                <MenuItem value="Alpha">Alpha</MenuItem>
                <MenuItem value="Bravo">Bravo</MenuItem>
                <MenuItem value="Charlie">Charlie</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Wristband Number Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 200 }}>
              <TextField
                id="wristband-number"
                label="Wristband Number"
                type="number"
                inputProps={{ min: 1, max: 80 }}
                value={selectedWristbandNumber || ""}
                onChange={(e) => {
                  const val =
                    e.target.value === ""
                      ? ""
                      : Math.max(1, Math.min(80, Number(e.target.value)));
                  setSelectedWristbandNumber(val === "" ? null : Number(val));
                }}
                fullWidth
              />
            </FormControl>
          </Grid>
          {/* Play Tag */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel id="play-tags-label">Play Tag</InputLabel>
              <Select
                labelId="play-tag-label"
                id="play-tag"
                value={selectedPlayTags || ""}
                label="Play Tag"
                onChange={handlePlayTagsChange}
              >
                {offensivePlayTags.map((playTag) => (
                  <MenuItem key={playTag.id} value={playTag.id}>
                    {playTag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};
